import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ScrollText, BookOpen, Trophy, Flame, CalendarDays } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DailyGoal from "../components/DailyGoal";

function Stat({ label, value, icon: Icon, testId }) {
  return (
    <div className="border border-border rounded-md bg-card p-5 paper-shadow" data-testid={testId}>
      <div className="flex items-center gap-2 font-ui text-xs tracking-caps text-muted-foreground">
        <Icon className="w-3.5 h-3.5 text-primary" /> {label}
      </div>
      <div className="font-display text-4xl tracking-tight mt-2">{value}</div>
    </div>
  );
}

function RankList({ title, rows, testId }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-border rounded-md bg-card p-5 paper-shadow" data-testid={testId}>
      <div className="font-ui text-xs tracking-caps text-muted-foreground mb-4">{title}</div>
      {rows.length === 0 && <div className="font-reader italic text-muted-foreground text-sm">open a few books to see this fill in.</div>}
      {rows.map((r) => (
        <div key={r.name} className="mb-3">
          <div className="flex justify-between font-ui text-sm"><span className="truncate">{r.name}</span><span className="text-muted-foreground">{r.count}</span></div>
          <div className="h-1.5 rounded-full bg-secondary mt-1"><div className="h-full rounded-full bg-primary" style={{ width: `${(r.count / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

const fmtTime = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

export default function Stats() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [metric, setMetric] = useState("pages");

  useEffect(() => { if (user) api.get("/stats/detailed").then((r) => setS(r.data)).catch(() => {}); }, [user]);

  if (!user) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <div className="font-display text-4xl tracking-tight">your reading story starts here.</div>
      <p className="font-reader italic text-muted-foreground mt-4">sign in to track your stats. <Link to="/" className="text-primary underline">browse the library →</Link></p>
    </div>
  );
  if (!s) return <div className="max-w-5xl mx-auto px-6 py-20 font-ui text-muted-foreground">tallying the pages…</div>;

  const chart = s.last_30_days.map((d) => ({ ...d, label: d.date.slice(5) }));

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 pb-32" data-testid="stats-page">
      <h1 className="font-display text-4xl md:text-5xl tracking-tight lowercase">reading stats</h1>
      <p className="font-reader text-muted-foreground mt-2 italic">every page you&apos;ve turned, counted with care.</p>

      <DailyGoal className="mt-8" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
        <Stat label="time read" value={fmtTime(s.total_minutes)} icon={Clock} testId="stats-time" />
        <Stat label="pages" value={s.total_pages.toLocaleString()} icon={ScrollText} testId="stats-pages" />
        <Stat label="started" value={s.books_started} icon={BookOpen} testId="stats-started" />
        <Stat label="finished" value={s.books_finished} icon={Trophy} testId="stats-finished" />
        <Stat label="best streak" value={`${s.longest_streak_days}d`} icon={Flame} testId="stats-best-streak" />
        <Stat label="active days" value={s.active_days} icon={CalendarDays} testId="stats-active-days" />
      </div>

      <div className="border border-border rounded-md bg-card p-5 paper-shadow mt-6" data-testid="stats-chart">
        <div className="flex items-center justify-between mb-4">
          <div className="font-ui text-xs tracking-caps text-muted-foreground">last 30 days</div>
          <div className="flex gap-1">
            {["pages", "minutes"].map((m) => (
              <button key={m} onClick={() => setMetric(m)} data-testid={`stats-metric-${m}`}
                className={`font-ui text-xs tracking-caps px-3 py-1 rounded-full border transition-colors ${metric === m ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{m}</button>
            ))}
          </div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart}>
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} stroke="hsl(var(--muted-foreground))" />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={30} stroke="hsl(var(--muted-foreground))" />
              <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
              <Bar dataKey={metric} fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <RankList title="favorite genres" rows={s.top_genres} testId="stats-top-genres" />
        <RankList title="most-read authors" rows={s.top_authors} testId="stats-top-authors" />
      </div>
    </div>
  );
}
